import { Facets, GetFFProductsParams } from 'commerce/factFinder/types';
import { adaptFFQueryParameters } from './queryParameters';

const multiValueFilter =
  (name: Facets, operator: 'and' | 'or' = 'or') =>
  (values: string[]) => ({
    name,
    multiplyValues: { operator, values },
  });

const singleValueFilter = (name: Facets) => (value: string) => ({
  name,
  values: [{ type: 'string', value_1: value }],
});

const rangeFilter =
  (name: Facets) =>
  ([min, max]: number[]) => ({
    name,
    values: [
      {
        type: 'interval',
        value_1: min,
        value_2: max,
        start: 'enclosed',
        end: 'not_enclosed',
      },
    ],
  });

export const filtersMap: Record<Facets, any> = {
  [Facets.PRICE]: rangeFilter(Facets.PRICE),
  [Facets.CATEGORY_NAMES]: singleValueFilter(Facets.CATEGORY_NAMES),
  [Facets.TITLE]: singleValueFilter(Facets.TITLE),
  [Facets.CATEGORY]: singleValueFilter(Facets.CATEGORY),
  [Facets.BRAND]: multiValueFilter(Facets.BRAND, 'or'),
  [Facets.COLORS]: multiValueFilter(Facets.COLORS),
  [Facets.TYPES]: multiValueFilter(Facets.TYPES),
  [Facets.MATERIALS]: multiValueFilter(Facets.MATERIALS),
  [Facets.RATING]: rangeFilter(Facets.RATING),
  [Facets.SIZE]: multiValueFilter(Facets.SIZE),
  [Facets.GENDERS]: multiValueFilter(Facets.GENDERS),
  [Facets.TAGS]: multiValueFilter(Facets.TAGS),
};

const getFFBasicAdditionalParams = () => ({
  excludeProductsNotInRange: true,
  useDeduplication: true,
});

const getFFCommonParams = (
  params?: GetFFProductsParams,
  isSearch: boolean = false,
) => {
  let filter = params?.filters
    ? Object.entries(params.filters).map(([name, data]) =>
        filtersMap[name as Facets](data),
      )
    : [];

  if (isSearch) {
    filter = filter.filter((f) => f.name !== Facets.TITLE);
  }

  return filter;
};

export const getFFNavigationParams = (params?: GetFFProductsParams) => {
  const filter = getFFCommonParams(params, false);

  const substringFilters = [...filter];

  const hasTitleFilter = filter.some((f) => f.name === Facets.TITLE);

  if (params?.query && !hasTitleFilter) {
    substringFilters.push(filtersMap[Facets.TITLE](params.query));
  }

  if (params?.categoryName) {
    substringFilters.push(
      filtersMap[Facets.CATEGORY_NAMES](params.categoryName),
    );
  }

  const queryParams = adaptFFQueryParameters(
    {
      filter: substringFilters.flat(),
      additionalParams: {
        page: params?.page || 1,
        hitsPerPage: params?.perPage || 60,
        ...(params?.query ? { fss: params.query } : {}),
        ...(params?.sortBy
          ? { sort: `${params.sortBy.field}:${params.sortBy.direction}` }
          : {}),
        ...getFFBasicAdditionalParams(),
      },
    },
    false,
  );

  const searchParams = new URLSearchParams(queryParams.toString());

  if (params?.searchQuery) {
    params.searchQuery.forEach((value, key) => {
      searchParams.append(key, value);
    });
  }

  return searchParams;
};

export const getFFSearchParams = (params?: GetFFProductsParams) => {
  const filter = getFFCommonParams(params, true);

  const queryParams = adaptFFQueryParameters(
    {
      filter: filter.flat(),
      additionalParams: {
        query: params?.query || '',
        page: params?.page || 1,
        hitsPerPage: params?.perPage || 60,
        ...(params?.sortBy
          ? { sort: `${params.sortBy.field}:${params.sortBy.direction}` }
          : {}),
        ...getFFBasicAdditionalParams(),
      },
    },
    true,
  );

  const searchParams = new URLSearchParams(queryParams.toString());

  if (params?.searchQuery) {
    params.searchQuery.forEach((value, key) => {
      searchParams.append(key, value);
    });
  }

  return searchParams;
};
